import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, RouteObject, useLocation } from 'react-router-dom';
import { PublicLayout } from './components/layout/PublicLayout';
import { DashboardLayout } from './components/layout/DashboardLayout';

const lazy_ = (factory: () => Promise<{ default: React.ComponentType }>) => {
  const Comp = lazy(factory);
  return (
    <Suspense fallback={<div style={{ padding: '4rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>Loading…</div>}>
      <Comp />
    </Suspense>
  );
};

/**
 * Redirect that carries `?token=…` across, since the token IS the credential.
 * Exported for its test: a data router will not complete a navigation under
 * jsdom, so this is covered directly rather than through the route table.
 */
export function KeepQuery({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

/**
 * Exported separately from the router so tests can mount these same definitions
 * in a memory router. `createBrowserRouter` reads window.location when the
 * module loads, so a test that pushState's a path and renders the shared router
 * quietly renders "/" instead — passing while covering nothing.
 */
export const routes: RouteObject[] = [
  {
    element: <PublicLayout />,
    children: [
      { path: '/', element: lazy_(() => import('./pages/Landing')) },
      { path: '/marketplace', element: lazy_(() => import('./pages/marketplace/MarketplaceBrowse')) },
      { path: '/marketplace/:id', element: lazy_(() => import('./pages/marketplace/MarketplaceDetail')) },
      { path: '/companies/:id', element: lazy_(() => import('./pages/companies/CompanyProfile')) },
      { path: '/sanctuary', element: lazy_(() => import('./pages/sanctuary/SanctuaryBrowse')) },
      { path: '/sanctuary/:id', element: lazy_(() => import('./pages/sanctuary/SanctuaryDetail')) },
      { path: '/sanctuary/booking/:id', element: lazy_(() => import('./pages/sanctuary/BookingConfirm')) },
      { path: '/network', element: lazy_(() => import('./pages/Placeholder')) },
      { path: '/community', element: lazy_(() => import('./pages/community/Community')) },
      { path: '/community/posts/:id', element: lazy_(() => import('./pages/community/PostDetail')) },
      { path: '/profile/:username', element: lazy_(() => import('./pages/profile/PublicProfile')) },
      { path: '/solutions', element: lazy_(() => import('./pages/SolutionsCatalogue')) },
      { path: '/pricing', element: lazy_(() => import('./pages/Pricing')) },
      { path: '/about', element: lazy_(() => import('./pages/About')) },
      { path: '/blog', element: lazy_(() => import('./pages/Blog')) },
      { path: '/contact', element: lazy_(() => import('./pages/Contact')) },

      { path: '/auth/login', element: lazy_(() => import('./pages/auth/Login')) },
      { path: '/auth/register', element: lazy_(() => import('./pages/auth/Register')) },
      { path: '/auth/verify-email', element: lazy_(() => import('./pages/auth/VerifyEmail')) },
      { path: '/auth/forgot-password', element: lazy_(() => import('./pages/auth/ForgotPassword')) },
      { path: '/auth/reset-password', element: lazy_(() => import('./pages/auth/ResetPassword')) },
      // Both land signed-out on purpose: the confirm link may be opened in a
      // browser that never logged in, and the cancel link may be the only route
      // back for someone whose account has been taken over.
      { path: '/auth/confirm-email-change', element: lazy_(() => import('./pages/auth/ConfirmEmailChange')) },
      { path: '/auth/cancel-email-change', element: lazy_(() => import('./pages/auth/CancelEmailChange')) },

      // Verification and password-reset emails were sent with these unprefixed
      // paths, which match no route — every one of those links landed on an
      // error screen. email_service.py now sends /auth/*; these keep the links
      // already sitting in people's inboxes working, token and all.
      { path: '/verify-email', element: <KeepQuery to="/auth/verify-email" /> },
      { path: '/reset-password', element: <KeepQuery to="/auth/reset-password" /> },
    ],
  },
  {
    element: <DashboardLayout />,
    children: [
      { path: '/dashboard', element: lazy_(() => import('./pages/dashboard/Overview')) },
      { path: '/dashboard/payouts', element: lazy_(() => import('./pages/dashboard/Payouts')) },
      { path: '/dashboard/profile', element: lazy_(() => import('./pages/dashboard/Profile')) },
      { path: '/dashboard/listings', element: lazy_(() => import('./pages/dashboard/DashListings')) },
      { path: '/dashboard/companies', element: lazy_(() => import('./pages/dashboard/Companies')) },
      { path: '/dashboard/bookings', element: lazy_(() => import('./pages/dashboard/Bookings')) },
      { path: '/dashboard/hosting', element: lazy_(() => import('./pages/dashboard/Hosting')) },
      { path: '/dashboard/hosting/create', element: lazy_(() => import('./pages/dashboard/HostingForm')) },
      { path: '/dashboard/hosting/:id/edit', element: lazy_(() => import('./pages/dashboard/HostingForm')) },
      { path: '/dashboard/files', element: lazy_(() => import('./pages/dashboard/MyFiles')) },
      { path: '/dashboard/subscription', element: lazy_(() => import('./pages/dashboard/Subscription')) },
      { path: '/dashboard/solutions', element: lazy_(() => import('./pages/dashboard/Solutions')) },
    ],
  },
  {
    element: <PublicLayout />,
    children: [
      { path: '*', element: lazy_(() => import('./pages/NotFound')) },
    ],
  },
];

export const router = createBrowserRouter(routes);
